export interface InvoiceLineItem {
  id: string
  description: string
  quantity: number
  unit_price: number
  discount_amount: number
  line_total: number
  charge_type?: string
  item_type?: string
  billing_start_date?: string | null
  billing_end_date?: string | null
}

export interface InvoiceStatusConfig {
  bg: string
  text: string
  border: string
  label: string
}

export const formatInvoiceDate = (dateString: string) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

export const formatChargeType = (value?: string) => (value || "charge").replace(/_/g, " ")

export const getServiceDescription = (invoiceType: string | undefined, servicePlanName: string) => {
  const type = invoiceType?.toLowerCase()
  switch (type) {
    case "subscription":
      return `${servicePlanName} - Monthly Subscription`
    case "installation":
      return "Internet Installation Service"
    case "equipment":
      return "Network Equipment Purchase"
    case "add_on":
      return "Additional Service/Feature"
    case "refund":
      return "Payment Refund"
    case "deposit":
      return "Security Deposit"
    case "maintenance":
      return "Maintenance Service"
    case "late_fee":
      return "Late Payment Fee"
    case "upgrade":
      return "Service Upgrade"
    case "reconnection":
      return "Service Reconnection"
    default:
      return invoiceType
        ? invoiceType
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        : "Invoice"
  }
}

export const getInvoiceStatusConfig = (status: string): InvoiceStatusConfig => {
  switch (status.toLowerCase()) {
    case "paid":
      return { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "PAID" }
    case "pending":
      return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "PENDING" }
    case "overdue":
      return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "OVERDUE" }
    case "partially_paid":
      return { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "PARTIAL" }
    default:
      return {
        bg: "bg-slate-50",
        text: "text-slate-700",
        border: "border-slate-200",
        label: status.toUpperCase(),
      }
  }
}

export const getPaymentStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "paid":
      return "bg-emerald-100 text-emerald-800 border-emerald-200"
    case "pending":
      return "bg-amber-100 text-amber-800 border-amber-200"
    case "failed":
      return "bg-red-100 text-red-800 border-red-200"
    default:
      return "bg-slate-100 text-slate-700 border-slate-200"
  }
}

export const hasSubscriptionLine = (lineItems: InvoiceLineItem[] = []) =>
  lineItems.some((li) => (li.charge_type || "").toLowerCase() === "subscription")

export const isSubscriptionInvoice = (invoiceType: string | undefined, lineItems: InvoiceLineItem[] = []) =>
  (invoiceType || "").toLowerCase() === "subscription" || hasSubscriptionLine(lineItems)
